/**
 * Date formatting for the Spanish UI.
 *
 * The design shows dates as "24 ago 2026" and relative times as "hace 2 horas",
 * so those two shapes are what this module produces.
 */
import { ATTENTION_RULES } from '@kainos/shared';

const LOCALE = 'es-CL';

/**
 * The month abbreviations the design uses, spelled out here on purpose.
 *
 * `Intl` answers from whatever CLDR the runtime ships: es-CL renders
 * "12 sept 2026" and joins day and month with a hyphen ("24-ago"), and the
 * spelling moves between ICU versions. The approved screens say "24 ago 2026",
 * and a date should not read differently in two browsers.
 */
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const day = (date: Date) => String(date.getDate()).padStart(2, '0');

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * `YYYY-MM-DD` is a calendar date, not an instant.
 *
 * `new Date('2026-10-15')` reads it as UTC midnight, which in Chile (UTC-4) is
 * the evening of the 14th — so every due date, hito and fecha de término was
 * rendered one day early. Building the date from its parts pins it to local
 * midnight, where a day boundary is the same thing the person typed.
 * Timestamps (`lastUpdateAt`, `createdAt`) are real instants and keep the
 * standard parsing.
 */
function parse(value: string | Date | undefined): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;

  const parts = DATE_ONLY.exec(value);
  const date = parts
    ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
    : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** "24 ago 2026" */
export function formatDate(value: string | Date | undefined): string {
  const date = parse(value);
  if (!date) return '—';
  return `${day(date)} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/** "24 ago" — used where the year is obvious from context. */
export function formatDayMonth(value: string | Date | undefined): string {
  const date = parse(value);
  if (!date) return '—';
  return `${day(date)} ${MONTHS[date.getMonth()]}`;
}

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
];

const relative = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto', style: 'long' });

/** "hace 2 horas", "hace 3 días". */
export function formatRelative(value: string | Date | undefined, now: Date = new Date()): string {
  const date = parse(value);
  if (!date) return 'Sin actualización';

  const diff = date.getTime() - now.getTime();
  const absolute = Math.abs(diff);

  if (absolute < 60 * 1000) return 'hace instantes';

  for (const [unit, ms] of UNITS) {
    if (absolute >= ms) return relative.format(Math.round(diff / ms), unit);
  }
  return 'hace instantes';
}

/**
 * The "última actualización" line from the design: a relative time when the
 * activity is being reported on, an explicit warning when it has gone quiet.
 */
export function formatLastUpdate(
  value: string | undefined,
  staleAfterDays: number,
  now: Date = new Date(),
): string {
  if (!value) return 'Sin actualización';

  const days = Math.floor((now.getTime() - new Date(value).getTime()) / (24 * 60 * 60 * 1000));
  if (days >= staleAfterDays) {
    return `Sin actualización hace ${days} ${days === 1 ? 'día' : 'días'}`;
  }
  return formatRelative(value, now);
}

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Whole calendar days from `now` to `date`; negative when it already passed. */
function daysUntil(date: Date, now: Date): number {
  const diff = startOfDay(date).getTime() - startOfDay(now).getTime();
  return Math.round(diff / (24 * 60 * 60 * 1000));
}

/**
 * How urgent a due date is. Drives colour, so it is a tone and not a boolean:
 * "vence en tres días" and "venció hace tres días" are different problems.
 */
export type DueTone = 'overdue' | 'urgent' | 'soon' | 'normal' | 'closed';

export interface DueDateLabel {
  label: string;
  tone: DueTone;
}

const plural = (days: number) => (days === 1 ? 'día' : 'días');

/**
 * The due date as a list can read it: near dates say how near, far dates say
 * when. A closed activity keeps the date but drops the urgency — "atrasada" is
 * a lie once the work is done.
 */
export function formatDueDate(
  value: string | undefined,
  options: { now?: Date; closed?: boolean } = {},
): DueDateLabel {
  const { now = new Date(), closed = false } = options;

  const date = parse(value);
  if (!date) return { label: '—', tone: 'normal' };
  if (closed) return { label: formatDayMonth(date), tone: 'closed' };

  const days = daysUntil(date, now);

  if (days < 0) return { label: `Atrasada ${-days} ${plural(-days)}`, tone: 'overdue' };
  if (days === 0) return { label: 'Vence hoy', tone: 'urgent' };
  if (days === 1) return { label: 'Vence mañana', tone: 'urgent' };
  if (days <= ATTENTION_RULES.dueSoonWindowDays) {
    return { label: `Vence en ${days} ${plural(days)}`, tone: 'soon' };
  }
  return { label: `Vence ${formatDayMonth(date)}`, tone: 'normal' };
}

export function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}
